// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// The grant inventory — the Grants sub-view of the unified Integrations tab
// (docs/28 §/integrations). Lists app and person access as group cards (by
// integration or by access holder) with a table inside, and is the sole
// surface that approves / denies / revokes them.
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { User as UserIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { GroupCard } from '@/components/greenlight/GroupCard';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { LoadMore } from '@/components/greenlight/LoadMore';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DecisionDialog } from '@/components/permissions/DecisionDialog';
import { PermissionRow } from '@/components/permissions/PermissionRow';
import { FilterPill } from '@/components/greenlight/FilterPill';
import {
  applyQueueFilters,
  groupPermissions,
  parseQueueParams,
  statusCounts,
  type GroupBy,
  type PermissionGroup,
} from '@/components/permissions/queue-logic';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { useCursorPages } from '@/lib/useCursorPages';
import { useIsMobile } from '@/lib/useIsMobile';
import type { PermissionListItem, PermissionStatus } from '@greenlight/shared';

// The queue is cursor-paginated (docs/16). This view accumulates pages rather
// than swapping them: its status counts, entity options, and grouping are all
// derived from the loaded set, so a single page would silently misreport them.
const PAGE_SIZE = 50;

const grantKey = (item: PermissionListItem): string => item.id;

type Decision = { verb: 'approve' | 'deny' | 'revoke'; item: PermissionListItem } | null;

interface RowHandlers {
  decision: Decision;
  isPending: boolean;
  onApprove: (item: PermissionListItem) => void;
  onDeny: (item: PermissionListItem) => void;
  onRevoke: (item: PermissionListItem) => void;
}

// ---------------------------------------------------------------------------
// One group rendered as a card with its grants inside. Desktop: divided rows
// inside the GroupCard. Mobile: a lighter header strip + stacked grant cards
// (so a card-in-card never doubles borders below md).
// ---------------------------------------------------------------------------

function PermissionGroupCard({
  group,
  groupBy,
  isMobile,
  handlers,
}: {
  group: PermissionGroup;
  groupBy: GroupBy;
  isMobile: boolean;
  handlers: RowHandlers;
}): React.JSX.Element {
  // The card identity is the grouping entity; rows lead with the *other* one.
  const rowPrimary = groupBy === 'integration' ? 'accessHolder' : 'integration';
  const { decision, isPending } = handlers;

  const tileSize = isMobile ? 32 : 36;
  const tile =
    group.kind === 'integration' ? (
      <IntegrationTile slug={group.slug ?? ''} displayName={group.name} size={tileSize} />
    ) : group.kind === 'app' ? (
      <AppIconTile name={group.name} slug={group.slug ?? ''} size={tileSize} />
    ) : (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-gl bg-muted text-muted-foreground"
        style={{ width: tileSize, height: tileSize }}
        role="img"
        aria-label="Person access holder"
      >
        <UserIcon className="h-4 w-4" />
      </span>
    );

  // An integration card links back to its registry card; an app card links to
  // the app detail page; a person has no home page — the name renders unlinked.
  const homeHref =
    group.kind === 'integration'
      ? `/integrations?view=integrations&integration=${group.slug}`
      : group.kind === 'app'
        ? `/apps/${group.items[0]?.app_id ?? ''}`
        : null;

  const rows = group.items.map((p) => (
    <PermissionRow
      key={p.id}
      item={p}
      primary={rowPrimary}
      isMobile={isMobile}
      pending={isPending && decision?.item.id === p.id}
      onApprove={handlers.onApprove}
      onDeny={handlers.onDeny}
      onRevoke={handlers.onRevoke}
    />
  ));

  if (isMobile) {
    return (
      <div data-testid={`permission-group-${group.id}`}>
        <div className="mb-3 flex items-center gap-2 rounded-gl bg-muted px-3 py-2">
          {tile}
          {homeHref !== null ? (
            <Link to={homeHref} className="min-w-0 truncate text-sm font-semibold text-foreground">
              {group.name}
            </Link>
          ) : (
            <span className="min-w-0 truncate text-sm font-semibold text-foreground">
              {group.name}
            </span>
          )}
          {group.kind !== 'integration' && (
            <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              {group.kind === 'app' ? 'App' : 'Person'}
            </span>
          )}
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">
            {group.items.length}
          </span>
          {group.pendingCount > 0 && (
            <StatusBadge variant="pending">{group.pendingCount} pending</StatusBadge>
          )}
        </div>
        <div className="space-y-3">{rows}</div>
      </div>
    );
  }

  return (
    <GroupCard
      data-testid={`permission-group-${group.id}`}
      title={
        <div className="flex items-center gap-3">
          {tile}
          {homeHref !== null ? (
            <Link
              to={homeHref}
              className="text-[15px] font-semibold tracking-tight text-foreground underline-offset-2 hover:underline"
            >
              {group.name}
            </Link>
          ) : (
            <span className="text-[15px] font-semibold tracking-tight text-foreground">
              {group.name}
            </span>
          )}
          {group.kind !== 'integration' && (
            <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              {group.kind === 'app' ? 'App' : 'Person'}
            </span>
          )}
        </div>
      }
      count={group.items.length}
      countNoun="grant"
      pendingCount={group.pendingCount}
      mutedHeader
    >
      <div className="divide-y divide-border">{rows}</div>
    </GroupCard>
  );
}

// ---------------------------------------------------------------------------
// Grants inventory view
// ---------------------------------------------------------------------------

export function GrantsView(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  // The grant inventory splits table→cards at lg (1024px): tablets get cards so
  // the multi-column row never collapses into tall, narrow strips (docs/28).
  const isCardLayout = useIsMobile(1024);

  const [decision, setDecision] = React.useState<Decision>(null);
  const mutationGenRef = React.useRef(0);

  // Grant-id-keyed review (ADR-0029): the app-keyed routes are unreachable for
  // a user grant, so the queue always acts through /api/credential-grants/:id.
  const reviewers = {
    approve: api.permissions.approveById.useMutation(),
    deny: api.permissions.denyById.useMutation(),
    revoke: api.permissions.revokeById.useMutation(),
  };
  const isPending = decision ? reviewers[decision.verb].isPending : false;

  const parsed = parseQueueParams(searchParams);
  const groupBy = parsed.group;
  const selectedStatus: PermissionStatus | 'all' = parsed.status ?? 'all';
  const selectedEntity =
    groupBy === 'integration'
      ? parsed.integrationId
      : parsed.accessHolderId && parsed.accessHolderType
        ? `${parsed.accessHolderType}:${parsed.accessHolderId}`
        : undefined;
  const pendingOnly = parsed.pendingOnly ?? false;

  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const queryKey = ['permissions-queue', cursor ?? null];
  const query = api.permissions.queue.useQuery(
    queryKey,
    { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
    { queryKey },
  );
  const pages = useCursorPages<PermissionListItem>({
    query,
    cursor,
    onCursorChange: setCursor,
    getKey: grantKey,
    // Narrowing to one integration or access holder asks for everything about
    // it — including grants sorted behind the queue's pending prefix — so the
    // filtered set is walked to completion rather than left at whatever had
    // loaded. Filters stay client-side: the pills must count every status in
    // scope, and the picker every entity, which a narrowed read cannot supply.
    ...(selectedEntity !== undefined ? { autoLoadWhile: (): boolean => true } : {}),
    // A decision reorders the queue (a decided grant leaves the pending prefix
    // the server sorts first), so the whole keyset is re-walked.
    invalidateKey: ['permissions-queue'],
  });
  const { items, hasError } = pages;
  const isLoading = pages.isLoadingFirstPage;

  function update(next: {
    group?: GroupBy;
    status?: PermissionStatus | 'all';
    entity?: string | undefined;
    pendingOnly?: boolean;
  }): void {
    const p = new URLSearchParams(searchParams);
    if (next.group !== undefined) {
      p.set('group', next.group);
      // Entity filters are grouping-specific — drop them when the grouping flips.
      p.delete('integrationId');
      p.delete('holderId');
      p.delete('holderType');
      // Keep old deep links readable, but do not carry their app-only filter
      // into the access-holder view.
      p.delete('appId');
    }
    if ('status' in next) {
      if (next.status && next.status !== 'all') {
        p.set('status', next.status);
        p.delete('pending'); // a concrete status and pending-only are mutually exclusive
      } else {
        p.delete('status');
      }
    }
    if ('entity' in next) {
      if ((next.group ?? groupBy) === 'integration') {
        p.delete('holderId');
        p.delete('holderType');
        p.delete('appId');
        if (next.entity) p.set('integrationId', next.entity);
        else p.delete('integrationId');
      } else {
        p.delete('integrationId');
        if (next.entity) {
          const separator = next.entity.indexOf(':');
          const holderType = next.entity.slice(0, separator);
          const holderId = next.entity.slice(separator + 1);
          if ((holderType === 'app' || holderType === 'user') && holderId) {
            p.set('holderType', holderType);
            p.set('holderId', holderId);
            p.delete('appId');
          }
        } else {
          p.delete('holderId');
          p.delete('holderType');
          p.delete('appId');
        }
      }
    }
    if (next.pendingOnly !== undefined) {
      if (next.pendingOnly) {
        p.set('pending', '1');
        p.delete('status');
      } else {
        p.delete('pending');
      }
    }
    setSearchParams(p, { replace: true });
  }

  // Only the active grouping's entity filter applies, so a stale filter from a
  // bookmark or hand-edited URL cannot silently narrow rows or skew counts.
  const entityFilter =
    groupBy === 'integration'
      ? { integrationId: parsed.integrationId }
      : {
          accessHolderId: parsed.accessHolderId,
          accessHolderType: parsed.accessHolderType,
        };

  // Counts for the status pills reflect the entity-scoped set (not the status /
  // pending-only narrowing), so toggling a status never changes the pill totals.
  const entityScoped = applyQueueFilters(items, entityFilter);
  const counts = statusCounts(entityScoped);

  const visible = applyQueueFilters(items, {
    status: parsed.status,
    ...entityFilter,
    pendingOnly,
  });
  const groups = groupPermissions(visible, groupBy);

  // Distinct entities for the filter select, derived from the full result set.
  // The access-holder filter includes both app and person access. The option
  // value carries the holder type so an app and person with the same UUID can
  // never collapse into one filter choice.
  const entityOptions = React.useMemo(() => {
    const seen = new Map<string, { name: string; typeLabel: string }>();
    for (const item of items) {
      if (groupBy === 'integration') {
        if (!seen.has(item.integration_id)) {
          seen.set(item.integration_id, { name: item.integration_name, typeLabel: 'Integration' });
        }
        continue;
      }
      const id = `${item.principal_type}:${item.principal_id}`;
      const name =
        item.principal_type === 'app' ? (item.app_name ?? 'Unknown app') : item.requested_by;
      if (!seen.has(id)) {
        seen.set(id, { name, typeLabel: item.principal_type === 'app' ? 'App' : 'Person' });
      }
    }
    return Array.from(seen.entries())
      .map(([id, option]) => ({ id, ...option }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, groupBy]);

  const distinctPrincipals = React.useMemo(
    () => new Set(entityScoped.map((i) => `${i.principal_type}:${i.principal_id}`)).size,
    [entityScoped],
  );

  const onConfirm = async (reason: string | undefined): Promise<void> => {
    if (!decision) return;
    const { verb, item } = decision;
    const gen = ++mutationGenRef.current;
    const pastTense = { approve: 'approved', deny: 'denied', revoke: 'revoked' } as const;
    try {
      const res = await reviewers[verb].mutateAsync({
        params: { grantId: item.id },
        body: reason ? { reason } : {},
      });
      if (res.status >= 300) throw res;
      toast.success(`Permission ${pastTense[verb]}.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['permissions-queue'] }),
        ...(item.app_id !== null
          ? [queryClient.invalidateQueries({ queryKey: ['permissions', item.app_id] })]
          : []),
      ]);
      if (mutationGenRef.current === gen) setDecision(null);
    } catch (err) {
      toast.error(`Couldn't ${verb} permission: ${mutationErrorMessage(err)}`);
    }
  };

  const handlers: RowHandlers = {
    decision,
    isPending,
    onApprove: (item) => setDecision({ verb: 'approve', item }),
    onDeny: (item) => setDecision({ verb: 'deny', item }),
    onRevoke: (item) => setDecision({ verb: 'revoke', item }),
  };

  const STATUS_PILLS: Array<{ value: PermissionStatus | 'all'; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'granted', label: 'Granted' },
    { value: 'denied', label: 'Denied' },
    { value: 'revoked', label: 'Revoked' },
  ];

  const GROUP_TABS: Array<{ value: GroupBy; label: string }> = [
    { value: 'integration', label: 'By integration' },
    { value: 'accessHolder', label: 'By access holder' },
  ];

  // Counts are over the loaded pages. While more remain they are lower bounds,
  // and read as such — never as an org-wide total.
  const atLeast = (n: number): string => (pages.hasMore ? `${n}+` : String(n));

  return (
    <div className="space-y-6" data-testid="grants-view">
      {/* Summary line */}
      {!isLoading && !hasError && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[13px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full bg-[hsl(var(--status-amber))]"
              aria-hidden="true"
            />
            <span className="text-foreground">{atLeast(counts.pending)}</span> awaiting review
          </span>
          <span aria-hidden="true" className="text-border">
            |
          </span>
          <span>
            <span className="text-foreground">{atLeast(counts.all)}</span> grants
          </span>
          <span aria-hidden="true" className="text-border">
            |
          </span>
          <span>
            <span className="text-foreground">{atLeast(distinctPrincipals)}</span>{' '}
            {distinctPrincipals === 1 && !pages.hasMore ? 'access holder' : 'access holders'}
          </span>
        </div>
      )}

      <Tabs
        value={groupBy}
        onValueChange={(v) => update({ group: v as GroupBy })}
        activationMode="manual"
      >
        <TabsList
          aria-label="Group grants by"
          className="flex h-auto w-full justify-start gap-2 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 text-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {GROUP_TABS.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="-mb-px h-10 shrink-0 rounded-none border-b-[3px] border-transparent px-3 text-sm font-medium text-foreground hover:border-border data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:ring-0"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_PILLS.map(({ value, label }) => {
          const pillCount = value === 'all' ? counts.all : counts[value as PermissionStatus];
          const isSelected = !pendingOnly && selectedStatus === value;
          return (
            <FilterPill key={value} selected={isSelected} onClick={() => update({ status: value })}>
              {label} <span className="tabular-nums">{atLeast(pillCount)}</span>
            </FilterPill>
          );
        })}
        <FilterPill
          selected={pendingOnly}
          onClick={() => update({ pendingOnly: !pendingOnly })}
          data-testid="pending-only-toggle"
          className="gap-1.5"
        >
          <span className="h-2 w-2 rounded-full bg-[hsl(var(--status-amber))]" aria-hidden="true" />
          Pending only
        </FilterPill>
        <div className="ml-auto">
          <Select
            value={selectedEntity ?? 'all'}
            onValueChange={(v) => update({ entity: v === 'all' ? undefined : v })}
          >
            <SelectTrigger
              className="w-[200px]"
              aria-label={
                groupBy === 'integration' ? 'Filter by integration' : 'Filter by access holder'
              }
            >
              <SelectValue
                placeholder={groupBy === 'integration' ? 'All integrations' : 'All access holders'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {groupBy === 'integration' ? 'All integrations' : 'All access holders'}
              </SelectItem>
              {entityOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.typeLabel} · {opt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* State ladder */}
      {hasError ? (
        <Alert variant="destructive" data-testid="permissions-error">
          <AlertTitle>Couldn&apos;t load the permission inventory.</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the grants. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-3" data-testid="permissions-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-16 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No grants yet."
          description="When apps or people request access to an integration, the grant lands here for review."
        />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No grants match these filters."
          description={
            pages.hasMore
              ? 'None of the grants loaded so far match. Load more to keep looking, or try a different status or grouping.'
              : 'Try a different status, grouping, or clear the pending-only filter.'
          }
        />
      ) : (
        <div className="space-y-5" data-testid="permission-groups">
          {groups.map((group) => (
            <PermissionGroupCard
              key={group.id}
              group={group}
              groupBy={groupBy}
              isMobile={isCardLayout}
              handlers={handlers}
            />
          ))}
        </div>
      )}

      {!hasError && !isLoading && (
        <LoadMore
          hasMore={pages.hasMore}
          isLoadingMore={pages.isLoadingMore}
          failed={pages.loadMoreFailed}
          onLoadMore={pages.loadMore}
          onRetry={pages.retry}
          noun="grants"
          testId="grants-load-more"
        />
      )}

      <DecisionDialog
        verb={decision?.verb ?? null}
        item={decision?.item ?? null}
        open={decision !== null}
        isPending={isPending}
        onConfirm={onConfirm}
        onOpenChange={(o) => {
          if (!o) setDecision(null);
        }}
      />
    </div>
  );
}
